import React, { useEffect, useState, useRef, useCallback } from "react";
import { Coffee, Utensils, User, Presentation, LogIn, Briefcase, TimerReset } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Card, SectionTitle, Empty } from "@/components/ui/page";
import { StatusBadge, Pill } from "@/components/StatusBadge";
import { fmtClock, fmtDuration, fmtTime } from "@/lib/format";

const BREAK_BTNS = [
  { key: "short", label: "Short Break", icon: Coffee, color: "#F59E0B" },
  { key: "lunch", label: "Lunch", icon: Utensils, color: "#0EA5E9" },
  { key: "personal", label: "Personal", icon: User, color: "#8B5CF6" },
  { key: "meeting", label: "Meeting / Training", icon: Presentation, color: "#3B82F6" },
];

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export default function EmployeeDashboard() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const base = useRef({ at: Date.now(), elapsed: 0 });

  const load = useCallback(() => {
    api.get("/employee/dashboard").then((r) => {
      setD(r.data);
      base.current = { at: Date.now(), elapsed: r.data.active_break?.elapsed_seconds || 0 };
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  // poll every 15s to stay fresh across refresh/tabs
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  if (!d) return <div className="py-20 text-center text-slate-400">Loading…</div>;

  const active = d.active_break;
  const liveElapsed = active ? base.current.elapsed + Math.floor((Date.now() - base.current.at) / 1000) : 0;
  const allowed = active?.allowed_seconds || 1;
  const progress = Math.min(100, (liveElapsed / allowed) * 100);
  const over = !!active && liveElapsed > allowed;
  const warning = !!active && !over && liveElapsed >= allowed - 5 * 60;
  const ringColor = over ? "#EF4444" : warning ? "#F59E0B" : "#08A7A0";

  const startBreak = async (key) => {
    setBusy(true);
    try {
      await api.post("/breaks/start", { break_type: key });
      toast.success(`${BREAK_BTNS.find((b) => b.key === key).label} started`);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const endBreak = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/breaks/end");
      toast.success(`Break ended · ${fmtDuration(data.duration_seconds)}${data.exceeded_seconds > 0 ? ` (exceeded ${fmtDuration(data.exceeded_seconds)})` : ""}`);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const circ = 2 * Math.PI * 70;

  return (
    <div>
      {/* Hero */}
      <div className="mb-6 flex flex-col gap-4 rounded-2xl bg-[#0B1F33] p-6 text-white sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-teal-300">{greeting()},</p>
          <h1 className="font-display text-3xl font-extrabold">{d.user.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-300">
            <span className="rounded-full bg-white/10 px-3 py-1">{d.user.process || "—"}</span>
            <span className="rounded-full bg-white/10 px-3 py-1">{d.user.designation || "—"}</span>
            <span className="rounded-full bg-white/10 px-3 py-1">{d.user.shift || "—"}</span>
          </div>
        </div>
        <div className="text-left sm:text-right">
          <p className="text-xs uppercase tracking-wide text-slate-400">Current Status</p>
          <div className="mt-1"><StatusBadge status={over ? "excess" : d.current_status} testId="emp-current-status" /></div>
          <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-400"><LogIn className="h-3.5 w-3.5" /> Login {fmtTime(d.login_time)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Timer + actions */}
        <Card className="p-6 lg:col-span-2" testId="break-panel">
          <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center">
            <div className="relative flex h-48 w-48 shrink-0 items-center justify-center">
              <svg className="h-48 w-48 -rotate-90" viewBox="0 0 160 160">
                <circle cx="80" cy="80" r="70" fill="none" stroke="#E2E8F0" strokeWidth="12" />
                <circle
                  cx="80" cy="80" r="70" fill="none" stroke={ringColor} strokeWidth="12" strokeLinecap="round"
                  strokeDasharray={circ} strokeDashoffset={active ? circ - (progress / 100) * circ : circ}
                  style={{ transition: "stroke-dashoffset 1s linear, stroke 0.3s" }}
                />
              </svg>
              <div className="absolute text-center">
                {active ? (
                  <>
                    <p className="font-mono text-4xl font-bold tracking-tight" style={{ color: ringColor }}>{fmtClock(liveElapsed)}</p>
                    <p className="mt-1 text-xs font-semibold text-slate-500">{active.break_label}</p>
                    <p className="text-[11px] text-slate-400">limit {fmtDuration(allowed)}</p>
                  </>
                ) : (
                  <>
                    <Briefcase className="mx-auto h-8 w-8 text-emerald-500" />
                    <p className="mt-2 font-display text-lg font-bold text-[#0B1F33]">Working</p>
                    <p className="text-xs text-slate-400">No active break</p>
                  </>
                )}
              </div>
            </div>

            <div className="w-full flex-1">
              {over && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">RED ALERT · You have exceeded the {active.break_label} limit by {fmtDuration(liveElapsed - allowed)}.</div>}
              {warning && <div className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700">WARNING · Approaching your {active.break_label} limit.</div>}
              {active ? (
                <button data-testid="return-to-work" disabled={busy} onClick={endBreak} className="w-full rounded-xl bg-emerald-500 py-4 text-base font-bold text-white transition hover:bg-emerald-600 disabled:opacity-60">
                  <TimerReset className="mr-2 inline h-5 w-5" /> Return to Work
                </button>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {BREAK_BTNS.map((b) => (
                    <button
                      key={b.key} data-testid={`start-break-${b.key}`} disabled={busy} onClick={() => startBreak(b.key)}
                      className="flex flex-col items-start gap-2 rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:-translate-y-0.5 hover:shadow-md disabled:opacity-60"
                    >
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: `${b.color}1A` }}>
                        <b.icon className="h-5 w-5" style={{ color: b.color }} />
                      </div>
                      <span className="text-sm font-bold text-[#0B1F33]">{b.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Today stats */}
        <Card className="p-5" testId="today-stats">
          <SectionTitle>Today at a glance</SectionTitle>
          <div className="mt-4 space-y-3">
            <Stat label="Work Time" value={fmtDuration(d.work_seconds)} color="#10B981" />
            <Stat label="Break Used" value={fmtDuration(d.break_used_seconds)} color="#F59E0B" />
            <Stat label="Break Remaining" value={fmtDuration(d.break_remaining_seconds)} color="#08A7A0" />
            <div>
              <div className="mb-1 flex justify-between text-xs font-semibold text-slate-500">
                <span>Daily break usage</span>
                <span>{Math.round((d.break_used_seconds / d.daily_limit_seconds) * 100)}%</span>
              </div>
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-[#08A7A0]" style={{ width: `${Math.min(100, (d.break_used_seconds / d.daily_limit_seconds) * 100)}%` }} />
              </div>
            </div>
            <div className="pt-1"><Pill tone={d.attendance_status === "present" ? "green" : "red"}>Attendance: {d.attendance_status}</Pill></div>
          </div>
        </Card>
      </div>

      {/* Break history */}
      <Card className="mt-6 p-5" testId="break-history">
        <SectionTitle>Today's Break History</SectionTitle>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="pb-2.5">Break Type</th><th className="pb-2.5">Start</th><th className="pb-2.5">End</th>
                <th className="pb-2.5">Duration</th><th className="pb-2.5">Limit</th><th className="pb-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {d.breaks.length === 0 && <tr><td colSpan={6}><Empty label="No breaks taken today" /></td></tr>}
              {d.breaks.map((b) => (
                <tr key={b.id} className="border-b border-slate-100 last:border-0">
                  <td className="py-3 font-semibold text-[#0B1F33]">{b.break_label}</td>
                  <td className="font-mono text-xs">{fmtTime(b.start_time)}</td>
                  <td className="font-mono text-xs">{b.end_time ? fmtTime(b.end_time) : <Pill tone="amber">Active</Pill>}</td>
                  <td className="font-mono text-xs">{b.duration_seconds != null ? fmtDuration(b.duration_seconds) : "—"}</td>
                  <td className="font-mono text-xs text-slate-400">{fmtDuration(b.allowed_seconds)}</td>
                  <td>{b.status === "excess" ? <Pill tone="red">Excess (+{fmtDuration(b.exceeded_seconds)})</Pill> : b.status === "active" ? <Pill tone="amber">Active</Pill> : <Pill tone="green">Within limit</Pill>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2.5">
      <span className="text-sm text-slate-600">{label}</span>
      <span className="font-mono text-base font-bold" style={{ color }}>{value}</span>
    </div>
  );
}
